import React, { useEffect, useState } from "react";
import Table from "../../components/Table";
import AddDialog from "../../components/Modal/AddModal";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useAuth } from "../../context/AuthContext";
import TextInput from "../../components/Select/InputText";
import SelectInput from "../../components/Select/SelectInput";
import ToggleSwitch from "../../components/Select/ToggleSwitch";
import { addPackageService, deletePackageService, getPackages, updatePackageService } from "../../services/packagesService";
import { FaPencilAlt, FaTrash } from "react-icons/fa";
import { showErrorToast, showSuccessToast } from "../../services/toastService";
import { hasPermission } from "../../utils/permissions";
import { ActivityType, getActivityTypes } from "../../services/activityTypeService";

type RevenueAllocationForm = {
    _id?: string;
    activityType: string;
    name?: string;
    percentage: string | number;
};

const asNumber = (value: unknown, fallback = 0) => {
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string") {
        const parsed = Number(value);
        if (Number.isFinite(parsed)) return parsed;
    }
    return fallback;
};

const formatMoney = (value: unknown) =>
    asNumber(value).toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    });

const StatusCell = ({ value }: { value: boolean }) => (
    <span className={`px-2 py-1 rounded-md ${value ? "text-green-500" : "text-red-500"}`}>
        {value ? "Active" : "Inactive"}
    </span>
);

const ShowOnAppCell = ({ value }: { value: boolean }) => (
    <span className={`px-2 py-1 rounded-md ${value ? "text-blue-500" : "text-gray-500"}`}>
        {value ? "Yes" : "No"}
    </span>
);

const Packages = () => {
    const { user } = useAuth();
    const canEditPackages = hasPermission(user, "packages.write");
    const [tableData, setTableData] = useState([]);
    const [addPackage, setAddPackage] = useState(false)
    const [editingPackageId, setEditingPackageId] = useState<string | null>(null);
    const [activityTypes, setActivityTypes] = useState<ActivityType[]>([]);

    useEffect(() => {
        const fetchPackages = async () => {
            if (!user?.id) return;
            try {
                const [packages, activities] = await Promise.all([getPackages(), getActivityTypes()]);
                setTableData(packages);
                setActivityTypes(activities);

            } catch (err) {
                console.error("Failed to fetch packages", err)


            }
        }
        fetchPackages();
    }, [user?.id])

    const columns = [
        {
            Header: "Package Name",
            accessor: "name",
        },

        {
            Header: "Price",
            accessor: "price",
            Cell: ({ value }: { value: unknown }) => formatMoney(value),
        },
        {
            Header: "Duration",
            accessor: "duration",
            Cell: ({ row }) => `${row.original.durationValue} ${row.original.durationUnit}`, // Displaying duration with unit
        },
        {
            Header: "Features",
            accessor: "features",
            Cell: ({ row }) => row.original.features.join(", "), // Joining features as comma-separated
        },
        {
            Header: "Status",
            accessor: "status",
            Cell: StatusCell,
        },
        {
            Header: "Show on App",
            accessor: "showOnApp",
            Cell: ShowOnAppCell,
        },
        {
            Header: "PT",
            accessor: "isPtSession",
            Cell: ({ value }: { value: boolean }) => (
                <span className={`px-2 py-1 rounded-md ${value ? "text-blue-500" : "text-gray-500"}`}>
                    {value ? "Yes" : "No"}
                </span>
            ),
        },
        {
            Header: "PT Sessions",
            accessor: "numberOfPtSessions",
        },
        {
            Header: "Revenue Split",
            accessor: "revenueAllocations",
            Cell: ({ row }: { row: { original: any } }) => {
                const allocations = Array.isArray(row.original.revenueAllocations)
                    ? row.original.revenueAllocations
                    : [];
                return allocations.length
                    ? allocations.map((item: any) => `${item.name} ${formatMoney(item.percentage)}%`).join(" / ")
                    : "-";
            },
        },
        ...(canEditPackages
            ? [{
                Header: "Actions",
                accessor: "actions",
                Cell: ({ row }) => (
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            onClick={() => handleEditPackage(row.original)}
                            className="text-blue-500 hover:text-blue-700"
                            aria-label="Edit package"
                        >
                            <FaPencilAlt />
                        </button>
                        <button
                            type="button"
                            onClick={() => handleDeletePackage(row.original)}
                            className="text-red-500 hover:text-red-700"
                            aria-label="Delete package"
                        >
                            <FaTrash />
                        </button>
                    </div>
                ),
            }]
            : []),
    ];

    const formik = useFormik({
        initialValues: {
            name: "",
            durationValue: 1,
            durationUnit: "month",
            price: "",
            features: [],
            newFeature: "",
            status: false,
            showOnApp: false,
            isPtSession: false,
            numberOfPtSessions: "",
            hasRevenueAllocations: false,
            revenueAllocations: [] as RevenueAllocationForm[],
        },

        validationSchema: Yup.object({
            name: Yup.string().required("Package name is required"),
            durationValue: Yup.number().required("Duration value is required").min(1),
            durationUnit: Yup.string()
                .oneOf(["day", "week", "month", "year"])
                .required("Duration unit is required"),
            price: Yup.number().required("Price is required").min(0),
            features: Yup.array().min(1, "At least one feature is required"),
            status: Yup.boolean().required("Availability is required"),
            showOnApp: Yup.boolean().required("Show on app selection is required"),
            isPtSession: Yup.boolean().required("PT selection is required"),
            numberOfPtSessions: Yup.number()
                .transform((value, originalValue) =>
                    originalValue === "" || originalValue === null ? NaN : value
                )
                .when("isPtSession", {
                    is: true,
                    then: (schema) => schema.required("Number of PT sessions is required").min(1),
                    otherwise: (schema) => schema.notRequired(),
                }),
            hasRevenueAllocations: Yup.boolean(),
            revenueAllocations: Yup.array().of(Yup.object({
                _id: Yup.string().optional(),
                activityType: Yup.string().required("Activity type is required"),
                name: Yup.string().optional(),
                percentage: Yup.number()
                    .typeError("Allocation percentage is required")
                    .moreThan(0, "Allocation must be greater than 0")
                    .max(100, "Allocation cannot exceed 100%")
                    .required("Allocation percentage is required"),
            })).when(["hasRevenueAllocations", "isPtSession"], {
                is: (hasRevenueAllocations: boolean, isPtSession: boolean) => hasRevenueAllocations && !isPtSession,
                then: (schema) => schema
                    .min(1, "Add at least one revenue activity")
                    .test("allocation-total", "Revenue allocations must total exactly 100%", (allocations) => {
                        const total = (allocations || []).reduce((sum, allocation) => sum + Number(allocation?.percentage || 0), 0);
                        return Math.abs(total - 100) < 0.001;
                    })
                    .test("unique-activity-types", "An activity type can only be added once", (allocations) => {
                        const ids = (allocations || []).map((allocation) => allocation?.activityType).filter(Boolean);
                        return new Set(ids).size === ids.length;
                    }),
                otherwise: (schema) => schema,
            }),
        }),

        onSubmit: async (values) => {
            try {
                if (!user) {
                    showErrorToast("User not logged in");
                    return;
                }
                const payload = {
                    name: values.name,
                    durationValue: values.durationValue,
                    durationUnit: values.durationUnit,
                    price: values.price,
                    features: values.features,
                    status: values.status,
                    showOnApp: values.showOnApp,
                    isPtSession: values.isPtSession,
                    numberOfPtSessions: values.isPtSession
                        ? Number(values.numberOfPtSessions)
                        : 0,
                    revenueAllocations: values.hasRevenueAllocations && !values.isPtSession
                        ? values.revenueAllocations.map((allocation) => ({
                            ...(allocation._id ? { _id: allocation._id } : {}),
                            activityType: allocation.activityType,
                            percentage: Number(allocation.percentage),
                        }))
                        : [],
                };
                if (editingPackageId) {
                    const updatedPackage = await updatePackageService(editingPackageId, payload);
                    const updatedId = updatedPackage?._id ?? updatedPackage?.id ?? editingPackageId;
                    setTableData((prevData) =>
                        prevData.map((pkg: any) =>
                            (pkg._id ?? pkg.id) === updatedId ? { ...pkg, ...updatedPackage } : pkg
                        )
                    );
                } else {
                    const savedPackage = await addPackageService(payload);
                    setTableData((prevData) => [
                        {
                            _id: savedPackage._id,
                            ...savedPackage,
                        },
                        ...prevData,
                    ])
                }

                closePopUp();
            } catch (err) {
                console.error("Error adding package:", err);
                showErrorToast((err as any)?.response?.data?.error || "Failed to add package");
            }
        } // ✅ no semicolon here
    });
    const openPopUp = () => {
        setEditingPackageId(null);
        setAddPackage(true);
    };
    const closePopUp = () => {
        setAddPackage(false);
        setEditingPackageId(null);
        formik.resetForm(); // Reset form when closing
    };

    const handleEditPackage = (pkg: any) => {
        if (!pkg) return;
        setEditingPackageId(pkg._id ?? pkg.id ?? null);
        formik.setValues({
            name: pkg.name ?? "",
            durationValue: pkg.durationValue ?? 1,
            durationUnit: pkg.durationUnit ?? "month",
            price: pkg.price ?? "",
            features: Array.isArray(pkg.features) ? pkg.features : [],
            newFeature: "",
            status: Boolean(pkg.status),
            showOnApp: Boolean(pkg.showOnApp),
            isPtSession: Boolean(pkg.isPtSession),
            numberOfPtSessions:
                pkg.isPtSession && typeof pkg.numberOfPtSessions !== "undefined"
                    ? String(pkg.numberOfPtSessions)
                    : "",
            hasRevenueAllocations: Array.isArray(pkg.revenueAllocations) && pkg.revenueAllocations.length > 0,
            revenueAllocations: Array.isArray(pkg.revenueAllocations)
                ? pkg.revenueAllocations.map((allocation: any) => ({
                    _id: allocation._id,
                    activityType: allocation.activityType?._id ?? allocation.activityType ?? "",
                    name: allocation.name ?? "",
                    percentage: allocation.percentage ?? "",
                }))
                : [],
        });
        setAddPackage(true);
    };

    const handleDeletePackage = async (pkg: any) => {
        const packageId = pkg?._id ?? pkg?.id;
        if (!packageId) {
            showErrorToast("Package ID is missing");
            return;
        }

        const packageName = pkg?.name ? ` "${pkg.name}"` : "";
        if (!window.confirm(`Delete package${packageName}?`)) return;

        try {
            await deletePackageService(packageId);
            setTableData((prevData) => prevData.filter((item: any) => (item._id ?? item.id) !== packageId));
            showSuccessToast("Package deleted");
        } catch (err) {
            console.error("Failed to delete package:", err);
            showErrorToast("Failed to delete package");
        }
    };

    return (
        <div className="p-6 w-full">
            <h1 className="text-2xl font-bold mb-4">Packages</h1>
            <Table columns={columns} data={tableData} pageSize={5} add={canEditPackages} onOpen={openPopUp} showPagination={false} />

            <AddDialog title={editingPackageId ? "Edit Package" : "Add new Package"} isOpen={addPackage} onClose={closePopUp} maxWidth="max-w-3xl">
                <form onSubmit={formik.handleSubmit} className="flex flex-col gap-4">
                    <TextInput
                        label="Package Name"
                        type="text"
                        name="name"
                        value={formik.values.name}
                        onChange={formik.handleChange}
                        placeholder="Enter Package Name"
                    />
                    <TextInput
                        label="Duration"
                        type="number"
                        name="durationValue"
                        value={formik.values.durationValue}
                        onChange={formik.handleChange}
                        placeholder="e.g. 1, 3, 12"
                    />

                    <SelectInput
                        label="Unit"
                        name="durationUnit"
                        value={formik.values.durationUnit}
                        onChange={formik.handleChange}
                        options={[
                            { value: "day", label: "Day(s)" },
                            { value: "week", label: "Week(s)" },
                            { value: "month", label: "Month(s)" },
                            { value: "year", label: "Year(s)" },
                        ]}
                    />
                    <TextInput
                        label="Price"
                        type="number"
                        name="price"
                        value={formik.values.price}
                        onChange={formik.handleChange}
                        placeholder="Enter Package Price"
                    />
                    <div>
                        <label className="font-medium">Features</label>
                        <div className="flex gap-2 mb-2">
                            <TextInput
                                type="text"
                                name="newFeature"
                                placeholder="e.g. Cardio access"
                                value={formik.values.newFeature}
                                onChange={formik.handleChange}
                            />
                            <button
                                type="button"
                                onClick={() => {
                                    if (formik.values.newFeature.trim()) {
                                        formik.setFieldValue("features", [...formik.values.features, formik.values.newFeature.trim()]);
                                        formik.setFieldValue("newFeature", "");
                                    }
                                }}
                                className="bg-blue-500 text-white px-3 rounded"
                            >
                                Add
                            </button>
                        </div>
                        <ul className="text-sm space-y-1">
                            {formik.values.features.map((feature, index) => (
                                <li key={index} className="flex justify-between items-center bg-gray-100 px-2 py-1 rounded">
                                    <span>{feature}</span>
                                    <button
                                        type="button"
                                        className="text-red-500 text-xs"
                                        onClick={() =>
                                            formik.setFieldValue(
                                                "features",
                                                formik.values.features.filter((_, i) => i !== index)
                                            )
                                        }
                                    >
                                        Remove
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <ToggleSwitch
                        label="Status"
                        name="status"
                        checked={formik.values.status}
                        onChange={(e) => formik.setFieldValue("status", e.target.checked)}
                    />
                    <ToggleSwitch
                        label="Show on App"
                        name="showOnApp"
                        checked={formik.values.showOnApp}
                        onChange={(e) => formik.setFieldValue("showOnApp", e.target.checked)}
                    />
                    <ToggleSwitch
                        label="Is PT"
                        name="isPtSession"
                        checked={formik.values.isPtSession}
                        onChange={(e) => {
                            const checked = e.target.checked;
                            formik.setFieldValue("isPtSession", checked);
                            if (!checked) {
                                formik.setFieldValue("numberOfPtSessions", 0);
                            } else {
                                formik.setFieldValue("hasRevenueAllocations", false);
                                formik.setFieldValue("revenueAllocations", []);
                            }
                        }}
                    />
                    {formik.values.isPtSession && (
                        <TextInput
                            label="Number of PT Sessions"
                            type="number"
                            name="numberOfPtSessions"
                            value={formik.values.numberOfPtSessions}
                            onChange={formik.handleChange}
                            placeholder="e.g. 8, 12"
                        />
                    )}
                    {!formik.values.isPtSession && (
                        <>
                            <ToggleSwitch
                                label="Split Subscription Revenue"
                                name="hasRevenueAllocations"
                                checked={formik.values.hasRevenueAllocations}
                                onChange={(event) => {
                                    const enabled = event.target.checked;
                                    formik.setFieldValue("hasRevenueAllocations", enabled);
                                    if (enabled && formik.values.revenueAllocations.length === 0) {
                                        formik.setFieldValue("revenueAllocations", [{ activityType: "", name: "", percentage: "" }]);
                                    }
                                    if (!enabled) formik.setFieldValue("revenueAllocations", []);
                                }}
                            />
                            {formik.values.hasRevenueAllocations && (
                                <div className="space-y-3 rounded-md border border-gray-200 bg-gray-50 p-4">
                                    {!activityTypes.length && (
                                        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                                            No Activity Types are configured. Add Taekwondo, BJJ, or other activities in Settings → Activity Types first.
                                        </div>
                                    )}
                                    <div className="flex items-center justify-between gap-3">
                                        <div>
                                            <p className="font-semibold">Package revenue activities</p>
                                            <p className="text-xs text-gray-500">All activity allocations must total exactly 100%.</p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => formik.setFieldValue("revenueAllocations", [
                                                ...formik.values.revenueAllocations,
                                                { activityType: "", name: "", percentage: "" },
                                            ])}
                                            className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium hover:bg-gray-100"
                                        >
                                            Add activity
                                        </button>
                                    </div>
                                    {formik.values.revenueAllocations.map((allocation, index) => {
                                        const errors = (formik.errors.revenueAllocations as any)?.[index];
                                        const selectedActivityIds = formik.values.revenueAllocations
                                            .filter((_, itemIndex) => itemIndex !== index)
                                            .map((item) => item.activityType);
                                        return (
                                            <div key={allocation._id || index} className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,1fr)_180px_auto] md:items-end">
                                                <div>
                                                    <label className="mb-1 block text-sm font-medium">Activity Type</label>
                                                    <select
                                                        name={`revenueAllocations.${index}.activityType`}
                                                        value={allocation.activityType}
                                                        onChange={formik.handleChange}
                                                        onBlur={formik.handleBlur}
                                                        className="w-full rounded border border-gray-300 px-3 py-2"
                                                    >
                                                        <option value="">{allocation.name ? `Select activity type (was ${allocation.name})` : "Select activity type"}</option>
                                                        {activityTypes.map((activity) => (
                                                            <option key={activity._id} value={activity._id} disabled={selectedActivityIds.includes(activity._id)}>
                                                                {activity.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    {errors?.activityType && formik.submitCount > 0 && <p className="text-sm text-red-500">{errors.activityType}</p>}
                                                </div>
                                                <div>
                                                    <TextInput
                                                        label="Allocation %"
                                                        type="number"
                                                        name={`revenueAllocations.${index}.percentage`}
                                                        value={allocation.percentage}
                                                        onChange={formik.handleChange}
                                                        onBlur={formik.handleBlur}
                                                        min="0.01"
                                                        max="100"
                                                        step="0.01"
                                                    />
                                                    {errors?.percentage && formik.submitCount > 0 && <p className="text-sm text-red-500">{errors.percentage}</p>}
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => formik.setFieldValue(
                                                        "revenueAllocations",
                                                        formik.values.revenueAllocations.filter((_, itemIndex) => itemIndex !== index),
                                                    )}
                                                    className="rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        );
                                    })}
                                    <div className="flex items-center justify-between text-sm font-semibold">
                                        <span>Total allocation</span>
                                        <span>{formik.values.revenueAllocations.reduce((sum, allocation) => sum + Number(allocation.percentage || 0), 0)}%</span>
                                    </div>
                                    {typeof formik.errors.revenueAllocations === "string" && formik.submitCount > 0 && (
                                        <p className="text-sm text-red-500">{formik.errors.revenueAllocations}</p>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                    <button type="submit" className="bg-blue-500 text-white px-4 py-2 rounded">
                        {editingPackageId ? "Update Package" : "Add Package"}
                    </button>
                </form>

            </AddDialog>
        </div>
    );
};

export default Packages;
